<script setup lang="ts">
import {
  computed,
  getCurrentInstance,
  onMounted,
  ref,
  type Ref,
  toRefs,
  watch,
} from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { useLoadingSpinnerStore } from '@therobotcompany/robco-frontend-library';
import {
  ObjectRotator,
  RotationConfig,
  type RotationVector,
} from '@therobotcompany/robot-visualizer';
import cloneDeep from 'lodash.clonedeep';
import { noAmpersandRegexRule, requiredRule } from '@/helpers/FormRules';
import AdvancedSettingsInput from '@/components/robvision/AdvancedSettingsInput.vue';
import CustomBackgroundsInput from '@/components/robvision/CustomBackgroundsInput.vue';
import {
  type AdvancedSettings,
  DEFAULT_CAD_ADVANCED_SETTINGS,
  EMPTY_NEW_CAD_PROJECT,
  type NewCADProject,
  VisionProjectType,
} from '@/models/RobVision';
import {
  addAdvancedSettings,
  createPart,
  createVisionProject,
  loadPartTextures,
  transformPartToSTL,
  uploadCustomBackgrounds,
} from '@/api/RobVisionApi';
import { simplifyGlb } from '@/utils/simplifyGlb';
import gettext from '@/i18n/gettext';

const MAX_FILE_SIZE_MB = 15;
const MAX_FILE_SIZE_BYTES = MAX_FILE_SIZE_MB * 1024 * 1024;

const { $gettext } = gettext;

// add prop for minimum object size
const props = withDefaults(
  defineProps<{
    isCadOpen: boolean;
    projectName: string;
    /** Minimum allowed size per axis. Defaults to 10mm (0.01 m) */
    minObjectSizeMm?: number;
    /** Minimum allowed size per axis. Defaults to 2000mm (2.00 m ) */
    maxObjectSizeMm?: number;
  }>(),
  {
    minObjectSizeMm: 10,
    maxObjectSizeMm: 2000,
  },
);

const { isCadOpen, projectName } = toRefs(props);
const emit = defineEmits(['update:isCadOpen', 'part-created']);

const createError = ref('');
const loadingSpinnerStore = useLoadingSpinnerStore();
const createPartValid = ref(false);
const showAdvancedSettings = ref(false);
const backgroundFiles = ref<File[]>([]);
const backgroundsValid = ref(true);
const newPart = ref<NewCADProject>(cloneDeep(EMPTY_NEW_CAD_PROJECT));
const newPartFile: Ref<File | undefined> = ref(undefined);
const textures = ref<string[]>([]);
const objectShift: Ref<RotationVector> = ref([0, 0, 0]);
const isGlbFile = ref(false);
const advancedSettings = ref<AdvancedSettings>(
  cloneDeep(DEFAULT_CAD_ADVANCED_SETTINGS),
);

const objectScaleValues = [1 / 1000, 1 / 100, 1 / 10, 1, 10, 100, 1000];
const objectScaleIndex = ref(3); // default index for scale 1
const tickLabels = objectScaleValues.reduce(
  (acc: Record<number, string>, val: number, idx: number) => {
    acc[idx] = val.toString();
    return acc;
  },
  {},
);

const defaultColor = '#';
const customColor = 'custom_color';

const objectColor = ref<string>('');

// this controls popup visibility
const showColorDialog = ref(false);
// this is our currently picked color
const tempColor = ref('#000000');

function openCustomColorPicker() {
  tempColor.value = '#000000';
  showColorDialog.value = true;
}

// confirm handler – inserts HEX into the list (avoids duplicates)
function confirmCustomColor() {
  const hex = tempColor.value.toUpperCase();
  if (!textures.value.includes(hex)) {
    textures.value.push(hex);
  }
  newPart.value.texture = hex;
  newPart.value.rgba_value = hex;
  showColorDialog.value = false;
}

// cancel handler – keep previous non-sentinel selection
function cancelCustomColor() {
  showColorDialog.value = false;
}

const swatches = [
  ['#000000', '#ffffff', '#00000000'],
  ['#ff6666', '#ff0000', '#990000'],
  ['#66ff66', '#00ff00', '#009900'],
  ['#66ccff', '#0000ff', '#000099'],
  ['#d5b59c', '#a07558', '#7d471d'],
];

// defaults
newPart.value.texture = 'Metal';
newPart.value.rgba_value = defaultColor;

const objectScale = computed({
  get: () => objectScaleValues[objectScaleIndex.value],
  set: (newValue) => {
    objectScaleIndex.value = objectScaleValues.indexOf(newValue);
  },
});

const isMetal = (val: string) => String(val).toLocaleLowerCase() === 'metal';

// The object's size in metres, as reported by the visualizer
const objectSize = ref<[number, number, number]>([0, 0, 0]);

// --- Simplification state ---
// The user's uploaded file (or STL-converted), before any simplification.
// newPart.file may be swapped out with a simplified GLB; this stays constant until the user picks a new file.
const originalFile: Ref<File | null> = ref(null);
// Baseline GLB captured from the visualizer once after load — the slider always simplifies this.
const baselineGlb: Ref<File | null> = ref(null);
// User-controlled reduction percent (0 = no simplification, 90 = drop 90% of triangles).
const simplifyPercent = ref(0);
// Current size of newPart.file (baseline or simplified) in bytes.
const currentGlbSize = ref(0);
const simplifyError = ref('');
let baselineCaptureInFlight = false;
// When we're about to apply a programmatic simplifyPercent (e.g. initial auto-fit),
// skip the next debounced run of the slider watcher to avoid a redundant simplification.
let skipNextSimplifyWatch = false;

interface PartRotator {
  // neutralPose exports without the user's rotation/scale baked in — used
  // for the simplification baseline, which is fed back into the viewer.
  getPartAsGlb: (
    filename: string,
    neutralPose?: boolean,
  ) => Promise<File | null>;
}

const vmInstance = getCurrentInstance();

const getRotator = (): PartRotator | null => {
  const rotator = vmInstance?.refs?.objectRotator as PartRotator | undefined;
  if (!rotator || typeof rotator.getPartAsGlb !== 'function') {
    return null;
  }
  return rotator;
};

const baselineTooLarge = computed(
  () =>
    baselineGlb.value !== null && baselineGlb.value.size > MAX_FILE_SIZE_BYTES,
);
const currentTooLarge = computed(
  () => currentGlbSize.value > MAX_FILE_SIZE_BYTES,
);

const formatMb = (bytes: number) => (bytes / (1024 * 1024)).toFixed(1);

const captureBaseline = async () => {
  if (baselineCaptureInFlight) {
    return;
  }
  if (baselineGlb.value !== null) {
    return;
  }
  if (!originalFile.value) {
    return;
  }
  const rotator = getRotator();
  if (!rotator) {
    return;
  }
  baselineCaptureInFlight = true;
  try {
    // Neutral pose: this baseline is simplified and swapped back into the
    // viewer, which re-applies the user's transform (preserve-transform).
    const glb: File | null = await rotator.getPartAsGlb('part.glb', true);
    if (!glb) {
      return;
    }
    if (glb.size > MAX_FILE_SIZE_BYTES) {
      // Auto-simplify before exposing baseline to the UI, so the user sees a single
      // settled state instead of "too big" flashing before the slider kicks in.
      loadingSpinnerStore.setShowLoadingOverlay(true);
      try {
        const keepRatio = MAX_FILE_SIZE_BYTES / glb.size;
        const estimatedReduction = Math.min(
          90,
          Math.round((1 - keepRatio) * 100),
        );
        const ratio = 1 - estimatedReduction / 100;
        const simplified = await simplifyGlb(glb, ratio);
        baselineGlb.value = glb;
        newPart.value.file = simplified;
        currentGlbSize.value = simplified.size;
        skipNextSimplifyWatch = true;
        simplifyPercent.value = estimatedReduction;
      } finally {
        loadingSpinnerStore.setShowLoadingOverlay(false);
      }
    } else {
      baselineGlb.value = glb;
      currentGlbSize.value = glb.size;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    // The objectSize watcher only fires on change, so a failure here would
    // otherwise leave the simplification UI missing with no explanation.
    console.error('Baseline capture failed:', err);
    createError.value =
      err?.message || $gettext('Error preparing mesh simplification');
  } finally {
    baselineCaptureInFlight = false;
  }
};

// Trigger baseline capture the moment the visualizer reports a non-zero object size
// (which only happens after the mesh has actually loaded).
watch(objectSize, ([sx, sy, sz]) => {
  if (baselineGlb.value !== null) {
    return;
  }
  if (!originalFile.value) {
    return;
  }
  if (!(sx > 0 || sy > 0 || sz > 0)) {
    return;
  }
  captureBaseline();
});

const applySimplification = async (percent: number) => {
  if (!baselineGlb.value) {
    return;
  }
  simplifyError.value = '';
  if (percent === 0) {
    // Restore original
    if (originalFile.value && newPart.value.file !== originalFile.value) {
      newPart.value.file = originalFile.value;
    }
    currentGlbSize.value = baselineGlb.value.size;
    return;
  }
  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    const ratio = 1 - percent / 100;
    const simplified = await simplifyGlb(baselineGlb.value, ratio);
    newPart.value.file = simplified;
    currentGlbSize.value = simplified.size;
  } catch (err: unknown) {
    simplifyError.value =
      err instanceof Error ? err.message : $gettext('Simplification failed');
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
};

const debouncedApplySimplification = useDebounceFn(applySimplification, 250);

watch(simplifyPercent, (percent) => {
  if (skipNextSimplifyWatch) {
    skipNextSimplifyWatch = false;
    return;
  }
  debouncedApplySimplification(percent);
});

// computed warning info
const sizeCheckInfo = computed(() => {
  const [x, y] = objectSize.value;

  const xMm = x * 1000;
  const yMm = y * 1000;
  const minSizeMm = props.minObjectSizeMm;
  const maxSizeMm = props.maxObjectSizeMm;

  const axes = ['X', 'Y'];

  // Determine which axes are too small or too large (in mm)
  const tooSmallAxes = [xMm, yMm]
    .map((v, i) => (v < minSizeMm ? axes[i] : null))
    .filter(Boolean) as string[];

  const tooLargeAxes = [xMm, yMm]
    .map((v, i) => (v > maxSizeMm ? axes[i] : null))
    .filter(Boolean) as string[];

  // round to whole mm for display
  const r = (n: number) => String(Math.round(n));

  // Construct message based on the findings
  let message = '';
  if (tooSmallAxes.length) {
    message = $gettext(
      'Object is too small on %{axes}. Minimum per axis is %{min_mm}mm.',
      {
        axes: tooSmallAxes.join(', '),
        min_mm: r(minSizeMm),
      },
    );
  } else if (tooLargeAxes.length) {
    message = $gettext(
      'Object is too large on %{axes}. Maximum per axis is %{max_mm}mm.',
      {
        axes: tooLargeAxes.join(', '),
        max_mm: r(maxSizeMm),
      },
    );
  }

  return {
    tooSmall: tooSmallAxes.length > 0,
    tooLarge: tooLargeAxes.length > 0,
    tooSmallAxes,
    tooLargeAxes,
    message,
  };
});

function capitalizeFirstLetter(val: string) {
  return String(val).charAt(0).toUpperCase() + String(val).slice(1);
}

async function fetchPartTextures() {
  try {
    textures.value = await loadPartTextures();
    const index = textures.value.indexOf(customColor, 0);
    if (index > -1) {
      textures.value.splice(index, 1);
    }
    textures.value = textures.value.map(capitalizeFirstLetter);
  } catch (err: unknown) {
    console.error(err);
  }
}

function resetSimplificationState() {
  originalFile.value = null;
  baselineGlb.value = null;
  skipNextSimplifyWatch = true;
  simplifyPercent.value = 0;
  currentGlbSize.value = 0;
  simplifyError.value = '';
  // Reset reported object size so the objectSize watcher will fire again when
  // the next mesh finishes loading.
  objectSize.value = [0, 0, 0];
}

async function processPartFile() {
  objectScaleIndex.value = 3; // reset scale
  // A genuinely new file starts unrotated — the viewer preserves the shift
  // across file swaps and never resets it on its own (visualizer >= 3.1).
  objectShift.value = [0, 0, 0];
  createError.value = '';
  resetSimplificationState();
  if (!newPartFile.value) {
    newPart.value.file = undefined;
    return;
  }
  loadingSpinnerStore.setShowLoadingOverlay(true);
  try {
    const file = newPartFile.value;
    const filename = file.name;
    if (file.size > MAX_FILE_SIZE_BYTES) {
      createError.value = $gettext('File is too big. Max file size is 15MB');
      newPart.value.file = undefined;
    } else if (
      filename &&
      (filename.toLowerCase().endsWith('.stp') ||
        filename.toLowerCase().endsWith('.step'))
    ) {
      newPart.value.file = await transformPartToSTL(file);
      originalFile.value = newPart.value.file ?? null;
    } else {
      newPart.value.file = file;
      originalFile.value = file;
    }
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
  } catch (err: any) {
    createError.value = err.message || $gettext('Error processing part file');
  } finally {
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
}

const resetAndClose = () => {
  emit('update:isCadOpen', false);
  createError.value = '';
  newPartFile.value = undefined;
  newPart.value = cloneDeep(EMPTY_NEW_CAD_PROJECT);
  objectShift.value = [0, 0, 0];
  objectScaleIndex.value = 3;
  objectColor.value = '';
  backgroundFiles.value = [];
  resetSimplificationState();
};

/** coloured swatches */
function dotStyle(hex: string) {
  return {
    backgroundColor: hex,
    width: '14px',
    height: '14px',
    borderRadius: '50%',
    display: 'inline-block',
    marginRight: '8px',
  };
}

const addPart = async (
  getPartAsGlb: (filename: string) => Promise<File | null>,
) => {
  loadingSpinnerStore.setShowLoadingOverlay(true);
  const file: File | null = await getPartAsGlb('part.glb');

  if (file) {
    try {
      if (file.size > MAX_FILE_SIZE_BYTES) {
        const sizeMb = formatMb(file.size);
        createError.value = `${$gettext(
          'File is still too large after simplification:',
        )} ${sizeMb} MB. ${$gettext(
          'Increase the simplification percentage or upload a smaller file.',
        )}`;
        return;
      }
      const uuid = await createVisionProject(
        projectName.value,
        VisionProjectType.CADPicking,
      );
      // Upload the exported GLB without reassigning newPart.file: that ref is
      // bound to the viewer (v-model:object-file-or-url), and swapping it here
      // makes the visualizer reload + re-center the part, jumping the preview
      // off-center right as the user clicks Add.
      await createPart(uuid, { ...newPart.value, file });
      await addAdvancedSettings(uuid, advancedSettings.value);
      if (backgroundFiles.value.length > 0) {
        await uploadCustomBackgrounds(uuid, backgroundFiles.value);
      }
      emit('part-created', uuid);
      resetAndClose();
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
    } catch (err: any) {
      createError.value = err.message || $gettext('Error creating part');
    } finally {
      loadingSpinnerStore.setShowLoadingOverlay(false);
    }
  } else {
    createError.value = $gettext('Error creating part');
    loadingSpinnerStore.setShowLoadingOverlay(false);
  }
};

const getExt = (name?: string) => name?.split('.').pop()?.toLowerCase() ?? '';

watch(
  newPartFile,
  (file) => {
    const ext = getExt(file?.name);
    isGlbFile.value = ext === 'glb';

    // auto-uncheck if not GLB (also covers "no file" case)
    if (!isGlbFile.value) {
      advancedSettings.value.keepTextures = false;
    }
  },
  { immediate: true },
);

onMounted(async () => {
  await fetchPartTextures();
});
</script>

<template>
  <!-- root wrapper -->
  <div>
    <!-- “Add Part” dialog -->
    <!-- The card is height-capped (clearing the app header, which renders
         above the overlay) and scrolls its body, so the title and action
         buttons stay visible however tall the content gets (e.g. with the
         Advanced options open). Vuetify's `scrollable` is unsuitable here:
         it stretches the dialog to the full viewport height, pushing the
         title under the app header. -->
    <v-dialog
      v-model="isCadOpen"
      :max-width="newPart.file ? 1200 : 600"
      persistent
      :content-class="newPart.file ? 'add-part-overlay' : undefined"
      @update:model-value="resetAndClose"
    >
      <v-card class="d-flex flex-column add-part-card">
        <v-card-title class="d-flex justify-space-between align-center">
          <span class="text-truncate">
            {{ $gettext('Add Part') }}
            <span v-if="projectName" class="text-medium-emphasis">
              — {{ projectName }}
            </span>
          </span>
          <!-- Advanced Settings Toggle -->
          <v-btn
            variant="text"
            size="small"
            class="text-caption"
            @click="showAdvancedSettings = !showAdvancedSettings"
          >
            <v-icon start>
              {{ showAdvancedSettings ? 'mdi-chevron-up' : 'mdi-cog' }}
            </v-icon>
            {{ $gettext('Advanced') }}
          </v-btn>
        </v-card-title>
        <v-card-text class="add-part-body">
          <v-form v-model="createPartValid" class="mt-4">
            <v-row>
              <v-col :cols="newPart.file ? 6 : 12">
                <v-file-input
                  v-model="newPartFile"
                  class="mt-4"
                  :label="$gettext('Part File')"
                  show-size
                  accept=".obj, .glb, .stl, .stp, .step"
                  :hint="
                    $gettext('Supported types: .obj, .glb, .stl, .stp, .step')
                  "
                  @update:modelValue="processPartFile"
                ></v-file-input>
                <div v-if="newPart.file">
                  <div class="mt-4 mb-12 text-center w-100 font-weight-bold">
                    {{
                      $gettext(
                        'Rotate the part so that the surface you want the camera to use to recognize the part is clearly visible. It will be used to train the model.',
                      )
                    }}
                  </div>
                  <rotation-config v-model:shift="objectShift" class="my-4" />

                  <div class="mt-4 text-center w-100 font-weight-bold">
                    {{ $gettext('Set Object Scale') }}
                  </div>
                  <div class="mt-1 mb-4 text-center w-100">
                    {{
                      $gettext('Grid size is 1m × 1m, Object size:') +
                      ` [${objectSize
                        .map((n: number) => Math.round(n * 1000))
                        .join(' × ')}] mm`
                    }}
                  </div>

                  <!-- Warning/Limit hint. The slot always reserves the
                       alert's height so the slider below does not jump when
                       the message appears (ROI-1250). -->
                  <div class="size-alert-slot mb-4">
                    <v-alert
                      v-if="sizeCheckInfo.tooSmall || sizeCheckInfo.tooLarge"
                      type="error"
                      variant="tonal"
                      density="compact"
                    >
                      {{ sizeCheckInfo.message }}
                    </v-alert>
                  </div>

                  <v-slider
                    v-model="objectScaleIndex"
                    class="mt-4 mb-4"
                    :ticks="tickLabels"
                    show-ticks="always"
                    :step="1"
                    :min="0"
                    :max="objectScaleValues.length - 1"
                  >
                  </v-slider>

                  <!-- File-size warning + mesh simplification slider -->
                  <v-alert
                    v-if="baselineTooLarge"
                    :type="currentTooLarge ? 'warning' : 'info'"
                    variant="tonal"
                    density="compact"
                    class="mb-2"
                  >
                    {{
                      $gettext(
                        'Converted file is %{size} MB (limit %{max} MB). Use the slider to simplify the mesh.',
                        {
                          size: formatMb(baselineGlb?.size ?? 0),
                          max: String(MAX_FILE_SIZE_MB),
                        },
                      )
                    }}
                  </v-alert>
                  <div v-if="baselineTooLarge" class="mt-2">
                    <div class="d-flex justify-space-between text-caption">
                      <span>
                        {{ $gettext('Simplification') }}: {{ simplifyPercent }}%
                      </span>
                      <span v-if="simplifyError" class="text-error">
                        {{ simplifyError }}
                      </span>
                    </div>
                    <v-slider
                      v-model="simplifyPercent"
                      :min="0"
                      :max="90"
                      :step="1"
                      thumb-label
                      hide-details
                    />
                  </div>
                </div>

                <!-- texture dropdown; “Add a Custom Color” launches popup -->
                <v-autocomplete
                  v-model="newPart.texture"
                  :items="textures"
                  :hide-details="true"
                  :label="$gettext('Texture')"
                  :rules="[requiredRule, noAmpersandRegexRule]"
                  :disabled="advancedSettings.keepTextures"
                  class="my-4 w-100"
                >
                  <!-- Dropdown rows -->
                  <template v-slot:item="{ props, item }">
                    <v-list-item v-bind="props">
                      <template v-slot:prepend>
                        <!-- metal / non metal case -->
                        <span class="d-flex align-center">
                          <span
                            :class="{ 'metal-dot': isMetal(item.raw) }"
                            :style="
                              isMetal(item.raw) ? undefined : dotStyle(item.raw)
                            "
                          >
                          </span>
                          <!-- {{ item.raw }} -->
                        </span>
                      </template>
                    </v-list-item>
                  </template>

                  <!-- Currently selected value -->
                  <template v-slot:selection="{ item }">
                    <!-- metal / non metal case -->
                    <span class="d-flex align-center">
                      <span
                        :class="{ 'metal-dot': isMetal(item.raw) }"
                        :style="
                          isMetal(item.raw) ? undefined : dotStyle(item.raw)
                        "
                      >
                      </span>
                      {{ item.raw }}
                    </span>
                  </template>

                  <!-- “Add a Custom Color” appended row -->
                  <template v-slot:append-item>
                    <v-list-item @click="openCustomColorPicker">
                      {{ $gettext('Add a Custom Color') }}
                    </v-list-item>
                  </template>
                </v-autocomplete>
              </v-col>

              <v-col v-if="newPart.file" cols="6">
                <!-- No mr-16: the stacked right margins left ~128px of dead
                     space beside the viewer, making the dialog look
                     off-center. -->
                <!-- wheel.capture.stop: the viewer's OrbitControls zoom the
                     camera on wheel/two-finger-scroll, so scrolling the
                     dialog body silently re-frames the 3D scene. Blocking
                     the event before the canvas keeps the scroll a scroll. -->
                <div class="h-100 w-100" @wheel.capture.stop>
                  <!-- The rotator (visualizer >= 3.1) preserves the user's
                       rotation/scale across file swaps (mesh simplification
                       replaces newPart.file with a variant of the same
                       part); genuinely new uploads are reset in
                       processPartFile. Baselines export in neutral pose so
                       the re-applied rotation is not doubled. -->
                  <object-rotator
                    ref="objectRotator"
                    v-model:object-file-or-url="newPart.file"
                    v-model:object-shift="objectShift"
                    v-model:object-scale="objectScale"
                    v-model:object-color="newPart.texture"
                    :keep-textures="advancedSettings.keepTextures"
                    style="height: 100%"
                    :container-border-radius="16"
                    @update:objectSize="
                      (newObjectSize: [number, number, number]) => {
                        objectSize = newObjectSize;
                      }
                    "
                    @update:objectColor="
                      (newObjectColor: string) => {
                        objectColor = newObjectColor;
                      }
                    "
                  />
                </div>
                <div
                  v-if="baselineGlb"
                  class="mt-4 d-flex justify-space-between text-caption"
                >
                  <span>
                    {{ $gettext('Original:') }}
                    {{ formatMb(baselineGlb.size) }} MB
                  </span>
                  <span :class="{ 'text-error': currentTooLarge }">
                    {{ $gettext('Current:') }}
                    {{ formatMb(currentGlbSize) }} MB
                    <span v-if="simplifyPercent > 0">
                      ({{ $gettext('reduced') }} {{ simplifyPercent }}%)
                    </span>
                  </span>
                </div>
              </v-col>
            </v-row>
            <AdvancedSettingsInput
              v-if="showAdvancedSettings"
              :showCADOptions="true"
              :advancedSettings="advancedSettings"
              :isGlbFile="isGlbFile"
            ></AdvancedSettingsInput>
          </v-form>

          <CustomBackgroundsInput
            v-model="backgroundFiles"
            v-model:valid="backgroundsValid"
            class="mx-4 mt-4"
          />

          <v-alert v-if="createError" class="mx-4" type="error" outlined>
            {{ createError }}
          </v-alert>
        </v-card-text>
        <v-card-actions>
          <v-btn @click="resetAndClose">{{ $gettext('Close') }}</v-btn>
          <v-spacer />
          <v-tooltip
            v-if="
              sizeCheckInfo.tooSmall ||
              sizeCheckInfo.tooLarge ||
              currentTooLarge
            "
            location="top"
          >
            <template #activator="{ props: tip }">
              <v-btn
                v-bind="tip"
                color="primary"
                :disabled="
                  !createPartValid ||
                  newPart.file?.size === 0 ||
                  sizeCheckInfo.tooSmall ||
                  sizeCheckInfo.tooLarge ||
                  currentTooLarge ||
                  !backgroundsValid
                "
                @click="
                  () => {
                    const r = getRotator();
                    if (r) addPart(r.getPartAsGlb);
                  }
                "
              >
                {{ $gettext('Add') }}
              </v-btn>
            </template>
            {{
              currentTooLarge
                ? $gettext(
                    'File is larger than %{max} MB. Increase simplification.',
                    { max: String(MAX_FILE_SIZE_MB) },
                  )
                : sizeCheckInfo.message
            }}
          </v-tooltip>
          <v-btn
            v-else
            color="primary"
            :disabled="
              !createPartValid || newPart.file?.size === 0 || !backgroundsValid
            "
            @click="
              () => {
                const r = getRotator();
                if (r) addPart(r.getPartAsGlb);
              }
            "
          >
            {{ $gettext('Add') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Popup dialog with color picker -->
    <v-dialog v-model="showColorDialog" max-width="420">
      <v-card>
        <v-card-title>{{ $gettext('Choose color') }}</v-card-title>
        <v-card-text>
          <v-color-picker
            v-model="tempColor"
            :swatches="swatches"
            show-swatches
            mode="hexa"
            :modes="['hex', 'hexa']"
            elevation="0"
            width="100%"
          />
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn rounded="pill" @click="cancelCustomColor">
            {{ $gettext('Cancel') }}
          </v-btn>
          <v-btn color="primary" rounded="pill" @click="confirmCustomColor">
            {{ $gettext('Confirm') }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<style>
/* Unscoped: v-dialog teleports its content out of this component. Vuetify
   centers dialogs in the full viewport, but the app's 64px nav rail (drawn
   above the overlay) shrinks the visible area on the left — the asymmetric
   margins re-center the wide part-loaded dialog within the actual content
   area. The rail only exists above the sm breakpoint (960px, see
   MainNavigation's smAndDown handling), so the shift must not apply below. */
@media (min-width: 960px) {
  .add-part-overlay {
    margin-left: 88px !important;
    margin-right: 24px !important;
    /* Vuetify computes the content width assuming 24px margins on both
       sides (calc(100% - 48px)) — with 112px total margin the card would
       overflow the right edge on windows narrower than ~1312px. */
    width: calc(100% - 112px) !important;
  }
}
</style>

<style scoped>
.size-alert-slot {
  /* One compact single-line alert — reserved even while empty. */
  min-height: 40px;
}
.add-part-card {
  /* Clear the app header (~64px, rendered above the overlay) plus breathing
     room on both sides of the vertically centered dialog. */
  max-height: calc(100vh - 160px);
}
.add-part-body {
  overflow-y: auto;
  /* Flex children refuse to shrink below their content height without this,
     which would grow the card past its max-height instead of scrolling. */
  min-height: 0;
}
</style>
